import { useId, type ReactNode } from 'react';
import { useT } from '../i18n';

interface FieldProps {
  label: string;
  help?: string;
  error?: string | undefined;
  optional?: boolean;
  /** Matches the key used in the validation Errors map, for focus-first-error. */
  name: string;
  children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
    'data-field': string;
  }) => ReactNode;
}

/**
 * One labelled control with its help text and inline error.
 *
 * The control is rendered via a render-prop so each field keeps its native
 * element (input/select/textarea) rather than being wrapped in a generic
 * component that has to re-implement every native behaviour.
 */
export function Field({ label, help, error, optional, name, children }: FieldProps) {
  const t = useT();
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;

  // Both are announced when present; the error comes first so it is heard first.
  const describedBy = [error ? errorId : null, help ? helpId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional && <span className="field__optional">{t.step2.additionalOptional}</span>}
      </label>

      {children({
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy,
        'data-field': name,
      })}

      {help && (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}

      {error && (
        <p className="field__error" id={errorId}>
          {/* The colour is not the message. */}
          <span className="visually-hidden">{t.validation.fieldError}</span>
          <span aria-hidden="true">↳</span>
          {error}
        </p>
      )}
    </div>
  );
}
